import React, { ReactElement, RefObject, useRef, useState } from "react";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@NA/Components/ADIHeaderSearchPopover";
import { css } from "styled-components";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { SearchInputStyles } from "@NA/Widgets/Coveo/Header/CoveoSearchInput";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import CoveoSearchSuggestions from "@NA/Widgets/Coveo/Header/CoveoSearchSuggestions";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import SearchHistory, { SearchHistoryStyles } from "@NA/Widgets/Coveo/Header/CoveoSearchHistory";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CoveoAutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@NA/Widgets/Coveo/Header/CoveoAutocompleteProducts";
import throttle from "lodash/throttle";
import { getSearchHistory, SearchHistoryItem } from "@insite/client-framework/Services/AutocompleteService";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Product } from "@coveo/headless/commerce";
import { CoveoProductModel } from "@NA/Types/CoveoModel";
import { HasCoveoHooks, withCoveoHooks } from "@NA/Components/Coveo/Contexts/CoveoHooksContext";

interface OwnProps {
    popoverRef: RefObject<HTMLUListElement>;
    searchInput: ReactElement;
    searchInputStyles: SearchInputStyles;
    searchId: string;
    autocompletePositionFunction?: (element: RefObject<HTMLUListElement>) => PositionStyle;
    clearSearch: () => void;
    closeSignInDrawer: () => void;
    focusedSearchHistoryQuery?: string;
    query: string;
    closeAutocomplete: () => void;
    openDealerModal: (coveoProduct: CoveoProductModel) => void;
    onClose: () => void;
}

export interface PopoverStyles {
    popover?: PopoverPresentationProps;
    popoverQuery?: InjectableCss;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    keywordHistoryStack?: InjectableCss;
}

export const searchInputStyles: PopoverStyles = {
    popover: {
        transitionDuration: "short",
        toggle: false,
    },
    popoverQuery: {
        css: css`
            width: 100%;
            left: 0px !important;
            right: 0px !important;
            min-height: auto;
            max-height: max-content !important;
            border: 1px solid #bbbbbb;
            padding: 20px;
            border-radius: 3px;
            box-shadow: none;
            &#headerSearch-popover {
                max-height: max-content !important;
                min-height: 100%;
            }
            @media only screen and (max-width: 1080px) {
                &#headerSearch-popover {
                    max-height: max-content !important;
                    min-height: 100%;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        position: absolute !important;
                        top: 38px !important;
                        max-width: 400px;
                    `,
                    css`
                        position: absolute !important;
                        top: 38px !important;
                        max-width: 400px;
                    `,
                    css`
                        position: absolute !important;
                        top: 38px !important;
                        max-width: 400px;
                    `,
                ])}
        `,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
        css: css`
            position: absolute;
            display: flex;
            font-size: 12px;
            font-stretch: 100%;
            font-style: normal;
            font-weight: 600;
            line-height: normal;
            background: ${getColor("common.background")};
            top: 0;
            box-shadow: none;
            cursor: default;
            height: auto;
            max-height: 513px;
            overflow: hidden;
            min-height: auto !important;
            width: 100%;
            left: 50% !important;
            right: auto !important;
            margin: 0 auto;
            transform: translate(-50%, 0px);
            margin-top: 3px;
            &#headerSearch-popover {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-height: calc(100% - 0px) !important;
                            //max-height: max-content !important;
                        `,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            
            > div[class^="sc"] {
                overflow: auto;
                max-height: inherit !important;
                height: auto;
                min-height: auto !important;
                display: flex;
                border-radius: 3px;
                border: solid 1px ${getColor("common.disabled")};
                padding: 20px;
                flex: 1;
                gap: 0px;
                align-items: flex-start;
                align-content: flex-start;
                &::-webkit-scrollbar {
                    width: 4px;
                }

                &::-webkit-scrollbar-track {
                    background: ${getColor("common.background")};
                }

                &::-webkit-scrollbar-thumb {
                    background-color: ${getColor("common.border")};
                    border-radius: 3px;
                }
            }
            .autocomplete > div {
                padding: 0 20px;
                width: 30%;
                &:last-child:not(:only-child) {
                    padding-right:0px;
                }
                &:first-child:not(:only-child) {
                    padding-left:0px;
                }
                @media (min-width: 360px) and (max-width: 1079px) {
                    padding: 0 20px
                    &:last-child:not(:only-child) {
                        flex: inherit;
                    }
                }
                @media (min-width: 360px) and (max-width: 767px) {
                    padding: 0;
                }
                &:empty {
                    border: none;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 35%;
                        `,
                        null,
                        null,
                    ])}
                
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 100% !important;
                        height: auto;
                        margin-top: 1.5px;
                    `,
                    css`
                        max-height: 100% !important;
                        height: auto;
                        margin-top: 1.5px;
                    `,
                    css`
                        max-height: 100% !important;
                        height: auto;
                        margin-top: 0px;
                    `,
                    css`
                        max-width: 100% !important;
                        height: auto !important;
                    `,
                    css`
                        max-width: 1020px !important;
                        width: 1020px !important;
                        height: auto;
                        max-height: initial;
                        overflow: hidden !important;
                    `,
                ])}
        `,
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            position: relative;
            padding: 0px;
            width: 100%;
            min-width: 0px !important;
            max-width: 100%;
            > div {
                width: 100%;
            }

            @media screen and (max-width: 767px) {
                height: calc(100vh - 90px) !important;
                //height: auto !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: row !important;
                            gap: 15px;
                            height: auto;
                        `,
                        css`
                            flex-direction: row !important;
                            gap: 15px;
                            height: auto;
                        `,
                        css`
                            flex-direction: row !important;
                            gap: 15px;
                            height: auto;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 15px;
            flex: 1;
            width: 100%;

            &:last-child:not(:only-child) {
                border-left: 1px solid #ccc;
                width: 70%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        border: none !important;
                        padding: 0px !important;
                        &:last-child:not(:only-child) {
                            flex: unset;
                            width: 100%;
                        }
                    `,
                    css`
                        border: none !important;
                        padding: 0px !important;
                        &:last-child:not(:only-child) {
                            flex: unset;
                            width: 100%;
                        }
                    `,
                    css`
                        border: none !important;
                        padding: 0px !important;
                        width: 35%;
                        &:last-child:not(:only-child) {
                            width: 70%;
                            flex: unset;
                        }
                    `,
                    css`
                        &:only-child {
                            width: 100%;
                        }
                        width: 35%;
                        &:last-child:not(:only-child) {
                            width: 65%;
                        }
                    `,
                    css`
                        &:only-child {
                            width: 100%;
                        }
                        width: 35%;
                        &:last-child:not(:only-child) {
                            width: 65%;
                        }
                    `,
                ])}
        `,
    },
    autocompleteProductsStyles: {
        container: {
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
                margin-bottom: 10px;
                height: 100%;
                &:last-child {
                    border-bottom: none;
                }
            `,
            offsetCss: css`
                margin-bottom: 8px;
            `,
        },
    },
    searchHistoryStyles: {
        wrapper: {
            css: css`
                width: 100%;
                padding: 0px;
                @media (min-width: 360px) and (max-width: 1079px) {
                    width: 100%;
                }
            `,
        },
    },
    keywordHistoryStack: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 50%;
        `,
    },
};

const styles = searchInputStyles;

type CoveoSearchPopoverProps = OwnProps & HasCoveoHooks;

const DOWN_KEY = 40;
const UP_KEY = 38;

const CoveoSearchPopover = ({
    popoverRef,
    searchInput,
    searchInputStyles,
    searchId,
    autocompletePositionFunction,
    closeSignInDrawer,
    query,
    clearSearch,
    closeAutocomplete,
    openDealerModal,
    onClose,
    searchBox,
}: CoveoSearchPopoverProps) => {
    const [focusableAutocompleteItems, setFocusableAutocompleteItems] = useState<Product[]>([]);
    const [focusedAutocompleteItem, setFocusedAutocompleteItem] = useState<Product>();
    const [focusedSearchHistoryQuery, setFocusSearchHistoryQuery] = useState<string>();

    const listRef = useRef<HTMLUListElement>(null);

    const { state: searchBoxState } = searchBox;

    const getPopoverPosition = (element: RefObject<HTMLUListElement>) => {
        if (autocompletePositionFunction) {
            return autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[searchInputStyles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)]
                          .height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;
            if (window.innerWidth <= 767) {
                positionStyle.top += 30;
            }
        }

        return positionStyle;
    };

    const handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                moveFocus("next");
                break;
            case UP_KEY:
                moveFocus("prev");
                break;
            default:
                break;
        }
    };

    const moveFocus = throttle((direction: "prev" | "next") => {
        if (query) {
            const focusableQueue =
                direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
            const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as Product);
            const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
            setFocusedAutocompleteItem(newFocusedOption);
        } else {
            const searchHistoryItems = getSearchHistory();
            const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
            const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
            const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
            const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
            setFocusSearchHistoryQuery(newFocusedOption?.query);
        }
    }, 100);

    const handlePopoverClose = () => {
        setFocusedAutocompleteItem(undefined);
        setFocusSearchHistoryQuery(undefined);
        onClose();
    };

    return (
        <Popover
            {...(query ? styles.popover : styles.popoverQuery)}
            controlsId={`${searchId}-popover`}
            ref={popoverRef}
            insideRefs={[listRef]}
            popoverTrigger={searchInput}
            positionFunction={getPopoverPosition}
            wrapperProps={styles.popoverWrapper}
            contentBodyProps={
                {
                    ref: listRef,
                    ...styles.popoverContentBody,
                } as any
            }
            handleKeyDown={handlePopoverKeyDown}
            onClose={handlePopoverClose}
            onOpen={closeSignInDrawer}
        >
            <StyledWrapper {...styles.autocompleteWrapper} className="autocomplete">
                <StyledWrapper {...styles.keywordHistoryStack}>
                    {query?.length >= 2 && (
                        <CoveoSearchSuggestions
                            clearSearch={clearSearch}
                            closeAutocomplete={() => {
                                closeAutocomplete();
                                (popoverRef.current as any)?.closePopover();
                            }}
                            extendedStyles={styles.searchHistoryStyles}
                        />
                    )}
                    {!query && (
                        <SearchHistory
                            focusedQuery={focusedSearchHistoryQuery}
                            clearSearch={clearSearch}
                            closeAutocomplete={() => {
                                closeAutocomplete();
                                (popoverRef.current as any)?.closePopover();
                            }}
                            extendedStyles={styles.searchHistoryStyles}
                        />
                    )}
                </StyledWrapper>
                {query?.length >= 2 && (
                    <CoveoAutocompleteProducts
                        clearSearch={clearSearch}
                        closeAutocomplete={() => {
                            closeAutocomplete();
                            (popoverRef.current as any)?.closePopover();
                        }}
                        openDealerModal={openDealerModal}
                        extendedStyles={styles.autocompleteProductsStyles}
                        query={query}
                    />
                )}
            </StyledWrapper>
        </Popover>
    );
};

export default withCoveoHooks(CoveoSearchPopover);
